import type { EditorState } from "@tiptap/pm/state";
import { pmDocToParagraphs } from "./convert";
import { useDocumentStore } from "../store/documentStore";
import { useProjectStore } from "../store/projectStore";
import { fs } from "../data/fs";
import { Highlight } from "../types";
import { getCollabSession } from "../collab/session";
import { encodeDocState } from "../collab/seed";

/**
 * Last-persisted serialization of the active document's text and highlights,
 * used to skip no-op writes. This is a correctness fix, not just an
 * optimization: every write bumps the file's mtime, and cloud sync
 * (src/cloud/qdaSync.ts) resolves local-vs-remote by mtime. A spurious rewrite
 * makes the local copy look newer than a genuinely-newer remote one, so the
 * remote never downloads — and worse, the stale local copy uploads over it.
 * Opening a document used to rewrite highlights.json every time for exactly
 * this reason (setHighlights fires the autosave subscription). Reset per
 * document via markLoadedFromDisk on load.
 */
let lastSavedDocJson: string | null = null;
let lastSavedHighlightsJson: string | null = null;

/**
 * Record the just-loaded on-disk state as "already saved" so applying it to
 * the stores doesn't count as an edit and trigger a rewrite (which would bump
 * mtime and defeat sync's newer-wins download). Highlights are seeded to the
 * loaded value; the document snapshot is cleared so the first genuine edit
 * still writes. Call from every path that loads a document into the editor
 * (see tabs.applyActiveDocument / reloadActiveDocumentFromDisk).
 */
export function markLoadedFromDisk(highlights: Highlight[]): void {
  lastSavedHighlightsJson = JSON.stringify(highlights);
  lastSavedDocJson = null;
}

/** Serialize the PM doc back to QdaDocument.paragraphs and persist (option A). */
export async function persistFromEditorState(editorState: EditorState): Promise<void> {
  const { document: doc, setDocument } = useDocumentStore.getState();
  const projectName = useProjectStore.getState().projectName;
  if (!doc || !projectName) return;
  // Yjs lineage sidecar (see QdaDocument.ystate): captured from the live
  // session so the next open RESTORES these exact structs instead of minting
  // new ones — the property that makes reload/peer merges duplication-proof.
  // When no live session exists the field is DROPPED rather than carried over
  // from `...doc`: a stale ystate paired with newer content would restore the
  // older content on next open, which is worse than falling back to seeding.
  const live = getCollabSession(doc.id);
  const updated = {
    ...doc,
    paragraphs: pmDocToParagraphs(editorState.doc),
    // Sidecar that preserves table structure the flat paragraphs can't (see
    // QdaDocument.content). Written every save so the two never drift.
    content: editorState.doc.toJSON() as Record<string, unknown>,
    ystate: live ? encodeDocState(live.ydoc) : undefined,
  };
  const json = JSON.stringify(updated);
  // Skip no-op writes (e.g. an edit reverted back to the saved content) so we
  // don't bump document.json's mtime and mislead cloud sync — see the snapshot
  // comment above.
  if (json === lastSavedDocJson) return;
  setDocument(updated);
  await fs.writeFile(fs.buildPath(projectName, doc.id, "document.json"), updated);
  lastSavedDocJson = json;
}

/**
 * Highlights live alongside the document (a sibling `highlights.json`, same
 * folder as `document.json`) rather than inside the QdaDocument type itself —
 * this is the app's own durable copy, independent of whatever the yaqatw
 * plugin separately persists via saveXmlPart when it's connected.
 */
export function highlightsPath(projectName: string, fileId: string): string {
  return fs.buildPath(projectName, fileId, "highlights.json");
}

/** Persist the current in-memory highlights for the active document. No-op with nothing open. */
export async function persistHighlights(highlights: Highlight[]): Promise<void> {
  const doc = useDocumentStore.getState().document;
  const projectName = useProjectStore.getState().projectName;
  if (!doc || !projectName) return;
  const json = JSON.stringify(highlights);
  // Skip if unchanged since the last save/load. Without this, merely opening a
  // document (which sets the highlight store, firing the autosave subscription)
  // rewrote highlights.json and bumped its mtime every time — see
  // markLoadedFromDisk and the snapshot comment above.
  if (json === lastSavedHighlightsJson) return;
  await fs.writeFile(highlightsPath(projectName, doc.id), highlights);
  lastSavedHighlightsJson = json;
}

/**
 * Highlight consumers (buildDecorations in highlightPlugin.ts, hostBridge.ts,
 * docxExporter.ts, …) all read `h.attrs.*` with no guard — safe until now,
 * because highlights only ever came from live, well-formed RPC calls within
 * a session. Loading from disk means untrusted/possibly-stale data can enter
 * the store, and with no error boundary anywhere in this app, one malformed
 * entry reaching those unguarded reads blanks the whole page. Filter here,
 * at the single point disk data enters useHighlightStore, instead of
 * defensively guarding every consumer.
 */
function isWellFormedHighlight(h: unknown): h is Highlight {
  if (!h || typeof h !== "object") return false;
  const entry = h as Partial<Highlight>;
  if (typeof entry.id !== "string" || !entry.attrs || typeof entry.attrs !== "object") return false;
  const attrs = entry.attrs as Partial<Highlight["attrs"]>;
  return (
    typeof attrs.paragraphId === "string" &&
    typeof attrs.startOffset === "string" &&
    typeof attrs.endOffset === "string" &&
    typeof attrs.text === "string"
  );
}

/** Load a document's persisted highlights. Returns [] if none were ever saved (or the data is corrupt). */
export async function loadHighlightsForDocument(projectName: string, fileId: string): Promise<Highlight[]> {
  const data = await fs.readFile(highlightsPath(projectName, fileId));
  if (!Array.isArray(data)) return [];
  return data.filter(isWellFormedHighlight);
}
